/**
 * The one control for handing an artist their link.
 *
 * Programmers used to reach for whichever single-purpose button happened to be in front
 * of them, so the combined invite — the one that actually saves a round trip — went
 * mostly unused. This button inverts that: it offers the combined link by default
 * wherever an artist and a show are both known, and pushes the profile-only and
 * show-only links into a menu for the cases that genuinely need one half.
 *
 * It also owns the fiddly bits that used to be copy-pasted around the pages: the
 * "Copied!" timer, the clipboard fallback, and minting a token on demand so a missing
 * link is one click rather than a hunt for a checkbox in some other dialog.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Link2, Loader2 } from 'lucide-react';
import {
    getPerson,
    updateHappening,
    updatePerson,
    type Happening,
    type Person,
} from '@littlebooker/dal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
    personFromRow,
    resolveInviteLinks,
    type InviteLinkOption,
    type InviteLinkVariant,
} from './inviteLinks';

/** An additional artist or show this button can also produce a link for. */
export interface InviteLinkAlternate {
    person?: Person | null;
    /** Hydrated on demand, for rows that only carry an id. */
    personId?: string | null;
    happening?: Happening | null;
    label: string;
    /**
     * Offer this alternate's profile-only link rather than a combined one. Used for the
     * co-artists on a multi-artist show: a combined link carries the show's stub token,
     * so handing it to everyone would give each of them edit rights over the whole show.
     */
    profileOnly?: boolean;
}

export interface InviteLinkButtonProps {
    person?: Person | null;
    /** Hydrated with `getPerson` on first interaction — keeps list rendering free. */
    personId?: string | null;
    happening?: Happening | null;
    alternates?: InviteLinkAlternate[];
    appearance?: 'card' | 'inline' | 'icon';
    onCopied?: (variant: InviteLinkVariant, url: string) => void;
    /** Mint a missing token on click instead of disabling the button. */
    allowTokenGeneration?: boolean;
    className?: string;
    disabled?: boolean;
}

const writeToClipboard = async (url: string, promptLabel: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return;
    }
    if (typeof window !== 'undefined') {
        window.prompt(promptLabel, url);
    }
};

export function InviteLinkButton({
    person,
    personId,
    happening,
    alternates = [],
    appearance = 'inline',
    onCopied,
    allowTokenGeneration = true,
    className,
    disabled = false,
}: InviteLinkButtonProps) {
    const queryClient = useQueryClient();
    const [copiedVariant, setCopiedVariant] = useState<InviteLinkVariant | null>(null);
    const [busy, setBusy] = useState(false);
    // Person fetched lazily when the caller only had an id (list rows).
    const [hydratedPerson, setHydratedPerson] = useState<Person | null>(null);
    // Why the last click copied nothing, shown in place of the label.
    const [failure, setFailure] = useState<string | null>(null);

    // The dialog header stays mounted while the programmer moves between shows, so a person
    // hydrated for the previous show must not leak into this one's link.
    useEffect(() => {
        setHydratedPerson(null);
        setFailure(null);
    }, [personId, happening?.id]);

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const effectivePerson = person ?? hydratedPerson ?? null;

    const { primary, others } = useMemo(
        () => resolveInviteLinks(effectivePerson, happening, origin),
        [effectivePerson, happening, origin],
    );

    /** Resolve the person we act on, fetching once if we were only given an id. */
    const ensurePerson = useCallback(async (): Promise<Person | null> => {
        if (effectivePerson) return effectivePerson;
        if (!personId) return null;
        // getPerson returns the raw row shape; the link builders want the mapped one.
        const fetched = await getPerson(personId);
        const mapped = fetched ? personFromRow(fetched) : null;
        setHydratedPerson(mapped);
        return mapped;
    }, [effectivePerson, personId]);

    /**
     * Mint whatever token is missing, then rebuild the link. Only ever asks for a token
     * that is actually absent: `generateStubLink` / `generateInviteLink` mint
     * unconditionally in the DAL, so passing them for an existing token would silently
     * invalidate links already sitting in someone's inbox.
     */
    const mintAndResolve = useCallback(
        async (option: InviteLinkOption, basePerson: Person | null): Promise<string | null> => {
            if (!allowTokenGeneration || !option.mintable) return null;

            let nextPerson = basePerson;
            let nextHappening = happening ?? null;

            if (option.mintable === 'person' || option.mintable === 'both') {
                if (nextPerson && !nextPerson.invite_token) {
                    nextPerson = await updatePerson(nextPerson.id, { generateInviteLink: true });
                    setHydratedPerson(nextPerson);
                    queryClient.invalidateQueries({ queryKey: ['people'] });
                }
            }

            if (option.mintable === 'happening' || option.mintable === 'both') {
                if (nextHappening && !nextHappening.metadata?.stub_token) {
                    nextHappening = await updateHappening(nextHappening.id, { generateStubLink: true });
                    queryClient.invalidateQueries({ queryKey: ['happenings'] });
                }
            }

            const rebuilt = resolveInviteLinks(nextPerson, nextHappening, origin);
            if (option.variant === 'combined') return rebuilt.primary.url;
            return rebuilt.others.find((o) => o.variant === option.variant)?.url ?? null;
        },
        [allowTokenGeneration, happening, origin, queryClient],
    );

    const copy = useCallback(
        async (option: InviteLinkOption) => {
            if (busy) return;
            setBusy(true);
            setFailure(null);
            try {
                const basePerson = await ensurePerson();
                // Re-resolve against the hydrated person: a list row starts with no
                // person at all, so the option computed at render time can be stale.
                const resolved = resolveInviteLinks(basePerson, happening, origin);
                const fresh =
                    option.variant === 'combined'
                        ? resolved.primary
                        : (resolved.others.find((o) => o.variant === option.variant) ?? option);

                /*
                 * Never hand out a narrower link than the button promised. When the artist
                 * could not be loaded — a stale person_id left in the show's artist list by a
                 * deleted or merged person, or someone outside this account — the resolver
                 * falls back to the show-only link, and this used to copy it silently: the
                 * programmer pasted a show-only invite believing it was the combined one.
                 */
                if (option.variant === 'combined' && personId && fresh.variant !== 'combined') {
                    setFailure("Couldn't load this show's artist — re-pick them on the show");
                    return;
                }

                const url = fresh.url ?? (await mintAndResolve(fresh, basePerson));
                if (!url) {
                    setFailure("Couldn't build the invite link");
                    return;
                }

                await writeToClipboard(url, fresh.label);
                setCopiedVariant(option.variant);
                setTimeout(() => setCopiedVariant(null), 2000);
                onCopied?.(option.variant, url);
            } catch (error) {
                console.error('Failed to copy invite link', error);
                setFailure("Couldn't copy the invite link");
            } finally {
                setBusy(false);
            }
        },
        [busy, ensurePerson, happening, mintAndResolve, onCopied, origin, personId],
    );

    const copyAlternate = useCallback(
        async (alternate: InviteLinkAlternate) => {
            if (busy) return;
            setBusy(true);
            try {
                let target = alternate.person ?? null;
                if (!target && alternate.personId) {
                    const fetched = await getPerson(alternate.personId);
                    target = fetched ? personFromRow(fetched) : null;
                }
                // profileOnly drops the show from context, so the resolver yields the
                // artist's profile link rather than a combined one carrying the stub token.
                const counterpart = alternate.profileOnly
                    ? null
                    : (alternate.happening ?? happening ?? null);
                const resolved = resolveInviteLinks(target, counterpart, origin);
                const option = resolved.primary;

                let url = option.url;
                if (!url && option.mintable === 'person' && target && allowTokenGeneration) {
                    const minted = await updatePerson(target.id, { generateInviteLink: true });
                    queryClient.invalidateQueries({ queryKey: ['people'] });
                    url = resolveInviteLinks(minted, counterpart, origin).primary.url;
                }
                if (!url) return;

                await writeToClipboard(url, option.label);
                setCopiedVariant(option.variant);
                setTimeout(() => setCopiedVariant(null), 2000);
                onCopied?.(option.variant, url);
            } catch (error) {
                console.error('Failed to copy invite link', error);
            } finally {
                setBusy(false);
            }
        },
        [allowTokenGeneration, busy, happening, onCopied, origin, queryClient],
    );

    // A list row hasn't fetched its person yet, so `primary` looks show-only. Treat it as
    // combined-in-waiting rather than advertising the narrower link.
    const pendingHydration = !effectivePerson && Boolean(personId);
    const isCombined = primary.variant === 'combined' || pendingHydration;
    const needsMint = Boolean(primary.mintable) || pendingHydration;

    const copied = copiedVariant !== null;

    const primaryLabel = (() => {
        if (copied) return appearance === 'card' ? 'Invite link copied' : 'Copied!';
        if (failure) return failure;
        if (isCombined) {
            // Before the first click a list row has not loaded its artist, so it cannot know
            // whether a token is missing — say "create" only when it actually is.
            if (primary.mintable && allowTokenGeneration) return 'Create invite link & copy';
            return appearance === 'card' ? 'Invite link ready — profile + show' : 'Copy invite link';
        }
        return primary.label;
    })();

    const primaryTitle = isCombined
        ? `One link where ${effectivePerson?.name ?? 'the artist'} completes their profile and fills in the show details.`
        : primary.hint;

    const primaryDisabled =
        disabled || busy || (!primary.url && !needsMint) || (!primary.url && !allowTokenGeneration);

    const hasMenu = others.some((o) => o.url || o.mintable) || alternates.length > 0;

    const appearanceClasses = {
        card: 'flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-primary/40 px-2 py-1.5 text-xs font-medium text-primary hover:bg-primary/5',
        inline: 'inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80',
        icon: 'inline-flex items-center gap-1 text-primary hover:text-primary/80',
    }[appearance];

    return (
        <div
            className={cn('inline-flex items-center gap-1', appearance === 'card' && 'w-full', className)}
            // Cards and table rows are themselves clickable (they open the edit dialog).
            // Copying a link must not also navigate.
            onClick={(event) => event.stopPropagation()}
        >
            <button
                type="button"
                onClick={() => copy(primary)}
                disabled={primaryDisabled}
                className={cn(appearanceClasses, primaryDisabled && 'opacity-50 cursor-not-allowed', failure && 'text-destructive')}
                title={primaryTitle}
            >
                {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : copied ? (
                    <Check className="h-3.5 w-3.5" />
                ) : (
                    <Link2 className="h-3.5 w-3.5" />
                )}
                {appearance !== 'icon' && <span>{primaryLabel}</span>}
            </button>

            {hasMenu && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            disabled={disabled || busy}
                            className="inline-flex items-center rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
                            aria-label="Other invite links"
                            title="Other invite links"
                        >
                            <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Other links — use only if the artist needs one half</DropdownMenuLabel>
                        {others.map((option) => (
                            <DropdownMenuItem
                                key={option.variant}
                                disabled={!option.url && !(option.mintable && allowTokenGeneration)}
                                onSelect={(event) => {
                                    event.preventDefault();
                                    void copy(option);
                                }}
                                title={option.hint}
                            >
                                {option.label}
                            </DropdownMenuItem>
                        ))}
                        {alternates.length > 0 && <DropdownMenuSeparator />}
                        {alternates.map((alternate) => (
                            <DropdownMenuItem
                                key={alternate.label}
                                onSelect={(event) => {
                                    event.preventDefault();
                                    void copyAlternate(alternate);
                                }}
                            >
                                {alternate.label}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );
}
